import type { CSSProperties } from "react";
import { resolveAssetUrl } from "@heroncendre/player";
import habillage from "../habillage.json";
import { IconeExterne } from "./icones.js";
import { Signature } from "./Signature.js";
import type { CleInfo } from "./useInfos.js";
import * as s from "./infosStyles.js";

/**
 * ============================================================================
 *  ContenuCredits.tsx — Ce que disent les crédits, en un seul endroit
 * ----------------------------------------------------------------------------
 *  Le logo de l'Atelier dans les Nuages, le titre, les deux lignes de crédits, la
 *  signature et le bouton lien. Il est partagé par les deux fenêtres qui montrent les
 *  crédits — celle de l'accueil (`Credits`) et celle de la visite (`CreditsPanel`) — :
 *  elles ne diffèrent que par leur coque (voile, croix, focus), jamais par leur contenu.
 *
 *  Le logo est un média : sur R2, sa clé (`logoAtelier`) est écrite dans
 *  `src/habillage.json` par `pnpm habillage`. Il est rond ; son fond noir carré,
 *  sans transparence, est donc rogné par un `border-radius` plutôt que retouché.
 * ==========================================================================*/

export function ContenuCredits({
  t,
  assetBaseUrl,
  surLien,
  tailleLogo = 148,
}: {
  t: (cle: CleInfo) => string;
  assetBaseUrl: string;
  surLien: () => void;
  tailleLogo?: number;
}) {
  const { logoAtelier } = habillage as { logoAtelier?: string };

  return (
    <>
      {logoAtelier && (
        <img
          src={resolveAssetUrl(assetBaseUrl, logoAtelier)}
          alt={t("infoNomVisite")}
          width={tailleLogo}
          height={tailleLogo}
          style={logo(tailleLogo)}
        />
      )}
      <h2 style={s.titreCredits}>{t("infoCredits")}</h2>
      <div style={s.listeCredits}>
        <div style={s.ligneCredit}>
          <span style={s.roleCredit}>{t("infoRoleVisite")}</span>
          <span style={s.nomCredit}>{t("infoNomVisite")}</span>
        </div>
        <div style={{ ...s.ligneCredit, borderBottom: "1px solid rgba(246,238,227,.14)" }}>
          <span style={s.roleCredit}>{t("infoRoleLieu")}</span>
          <span style={s.nomCredit}>{t("infoNomLieu")}</span>
        </div>
      </div>
      <Signature t={t} lien={false} surLien={surLien} />
      <a
        className="inf-lien"
        href={t("infoLienUrl")}
        target="_blank"
        rel="noopener"
        aria-label={t("infoLienAria")}
        onClick={surLien}
        style={s.boutonLienCreme}
      >
        {t("infoLienBouton")}
        <IconeExterne taille={16} />
      </a>
    </>
  );
}

const logo = (taille: number): CSSProperties => ({
  width: taille,
  height: taille,
  flex: "none",
  borderRadius: "50%",
  objectFit: "cover",
  userSelect: "none",
  pointerEvents: "none",
});
